import { useMemo } from 'react'
import PropTypes from 'prop-types'

import EmptyCard from './EmptyCard'

import { LinkButton } from 'components/common/Button'
import CardWrapper from 'components/dashboard/CardWrapper'
import BusyOverlay from 'components/common/BusyOverlay'
import theme from 'theme'
import useAuth from 'hooks/use-auth'
import DonutChart, {
  DONUT_ATTACKS_COLOR_MAP
} from 'components/reports/DonutChart'
import { reportsTimePeriod } from 'utils/formatDate'
import useAgent from 'hooks/use-agent'
import TargetGraphLegend from 'components/attacks/create/targets/TargetGraphLegend'
import { getFormattedAttackType } from 'utils/metadata'
import { sortBy } from 'utils/list'

const DONUT_HEIGHT = 200
const DONUT_RADIUS = 70

function TotalAttacksRun({ filter, currentTeamId }) {
  let timePeriod = reportsTimePeriod({ duration: filter })
  const { data: allAttacks, loading } = useAgent(
    `/reports/attacks?end=${timePeriod.end}&period=${timePeriod.period}&start=${timePeriod.start}&teamId=${currentTeamId}`,
    {}
  )

  const allAttacksReportData = useMemo(() => {
    return Object.keys(allAttacks.data?.summary?.gremlins ?? {}).map(key => ({
      gremlin: key,
      count: allAttacks?.data?.summary?.gremlins[key]
    }))
  }, [allAttacks])

  const legendMap = useMemo(() => {
    return sortBy(
      allAttacksReportData.map(report => ({
        label: getFormattedAttackType(report.gremlin),
        text: report.count,
        color: DONUT_ATTACKS_COLOR_MAP[report.gremlin]
      })),
      report => report.text,
      'desc'
    )
  }, [allAttacksReportData])

  if (loading) return <BusyOverlay rounded shown />

  if (allAttacksReportData.length === 0) {
    return (
      <EmptyCard data-empty-report-attacks title="No Attacks Run">
        <LinkButton label="Run an attack" to="/attacks/new/" />
      </EmptyCard>
    )
  }

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        alignItems: 'center',
        gap: theme.space[5]
      }}
    >
      <DonutChart
        displaySingleData
        colorMap={DONUT_ATTACKS_COLOR_MAP}
        height={DONUT_HEIGHT}
        radius={DONUT_RADIUS}
        reportData={allAttacksReportData}
        rootId="#dashboardDonutAttacks"
        translate={{ left: 0, top: 0 }}
      />
      <TargetGraphLegend
        css={{
          gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))',
          height: 200,
          overflowY: 'scroll'
        }}
        items={legendMap}
      />
    </div>
  )
}

TotalAttacksRun.propTypes = {
  filter: PropTypes.string,
  currentTeamId: PropTypes.string.isRequired
}

export default function TotalAttacksRunCard() {
  const { currentTeamId } = useAuth()
  return (
    <CardWrapper
      defaultFilter="weekly"
      filters={[
        { value: 'weekly', label: 'This week' },
        { value: 'monthly', label: 'This month' }
      ]}
      overflow="hidden"
      renderCardContent={({ filter }) => (
        <TotalAttacksRun currentTeamId={currentTeamId} filter={filter} />
      )}
      title="Total Attacks Run"
    />
  )
}

TotalAttacksRunCard.propTypes = {
  infraClients: PropTypes.object,
  alfiClients: PropTypes.object,
  isLoading: PropTypes.bool,
  completedAttacks: PropTypes.array,
  activeAttacks: PropTypes.array
}
